import { EditIcon, TrashIcon } from "@xola/icons";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
    Experience,
    PriceVariation,
    PriceVariationType,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkTag,
    Schedule,
} from "types";
import { useCollection } from "../../../hooks/useCollection";
import { useResource } from "../../../hooks/useResource";
import { generatePriceVariationFromPurchaseRules } from "../../../models/PriceVariation";
import { getPurchaseRulesFromPurchaseRuleLink } from "../../../models/PurchaseRuleLink";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { CreatePriceVariation } from "./CreatePriceVariation";
import { SchedulePriceVariation } from "./SchedulePriceVariation";

interface PriceVariationProps {
    schedule: Schedule;
    basePriceVariation?: PriceVariation;
    onChange: (priceVariation: PriceVariation, purchaseRuleLink: PurchaseRuleLink) => void;
}

export const PriceVariations = ({ schedule, basePriceVariation, onChange }: PriceVariationProps) => {
    const [showEditPriceVariationModal, setShowEditPriceVariationModal] = useState(false);
    const [priceVariation, setPriceVariation] = useState<PriceVariation | null>(null);

    const { id: experienceId } = useParams<{ id?: ID }>();

    const { data: experience } = useResource<Experience>(["experiences", experienceId], null, {
        staleTime: 1000, // Cache for 1s so that its always re-fetched when the page loads
        keepPreviousData: true,
    });

    const { data: basePurchaseRuleLinks } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": PurchaseRuleLinkTag.PRICING,
        "entities.id": experienceId,
        expand: "purchaseRule",
        limit: 1,
    });

    const { data: schedulePurchaseRuleLinks } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": PurchaseRuleLinkTag.SCHEDULE,
        "entities.id": experienceId,
        expand: "purchaseRule",
        limit: 1,
    });

    const basePurchaseRuleLink = basePurchaseRuleLinks[0] ?? {};
    const schedulePurchaseRuleLink = schedulePurchaseRuleLinks[0] ?? {};

    const basePurchaseRules = getPurchaseRulesFromPurchaseRuleLink(basePurchaseRuleLink);
    const schedulePurchaseRules = getPurchaseRulesFromPurchaseRuleLink(schedulePurchaseRuleLink).filter(
        (purchaseRule) => !purchaseRule.deletedAt,
    );

    const scheduleLinkedPurchaseRules =
        schedulePurchaseRules.filter((purchaseRule) =>
            schedule.id ? purchaseRule.meta?.schedules?.includes(schedule.id) : false,
        ) ?? [];

    const setBasePriceVariation = () => {
        setPriceVariation(
            basePriceVariation ?? generatePriceVariationFromPurchaseRules(basePurchaseRules, PriceVariationType.BASE),
        );
    };

    // Do NOT change the order of he purchase rules, schedule linked rules should always be the last ones
    const setAdjustmentPriceVariation = () => {
        setPriceVariation(
            generatePriceVariationFromPurchaseRules(
                [...basePurchaseRules, ...scheduleLinkedPurchaseRules],
                PriceVariationType.ADJUSTMENT,
            ),
        );
    };

    useEffect(() => {
        if (scheduleLinkedPurchaseRules.length > 0) {
            setAdjustmentPriceVariation();
        } else if (basePurchaseRules.length > 0 || basePriceVariation) {
            setBasePriceVariation();
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [scheduleLinkedPurchaseRules.length, basePurchaseRules.length]);

    const handlePriceVariationChange = (priceVariation: PriceVariation) => {
        setPriceVariation(priceVariation);
        onChange(priceVariation, schedulePurchaseRuleLink);
    };

    const deletePriceVariation = () => {
        setBasePriceVariation();
        onChange(
            generatePriceVariationFromPurchaseRules(basePurchaseRules, PriceVariationType.BASE),
            schedulePurchaseRuleLink,
        );
    };

    const handlePurchaseRuleGroupChange = (purchaseRuleGroup: PurchaseRule[]) => {
        // Setting price variation with Base prices
        setPriceVariation(
            generatePriceVariationFromPurchaseRules(
                [...basePurchaseRules, ...purchaseRuleGroup],
                PriceVariationType.ADJUSTMENT,
            ),
        );
        // Saving price variation with updated prices
        onChange(
            generatePriceVariationFromPurchaseRules(purchaseRuleGroup, PriceVariationType.ADJUSTMENT),
            schedulePurchaseRuleLink,
        );
    };

    return basePurchaseRules.length > 0 || basePriceVariation ? (
        <div className="flex flex-col">
            <div className="mt-4 mb-2 flex items-center justify-between">
                <span className="text-sm">
                    <span className="font-bold">Price Variation</span>
                    <NoTranslate className="text-gray-darker">
                        &nbsp;{priceVariation?.type === PriceVariationType.ADJUSTMENT ? `(${priceVariation.name})` : ""}
                    </NoTranslate>
                </span>

                {priceVariation?.type === PriceVariationType.ADJUSTMENT && experience && (
                    <div className="flex space-x-2">
                        <SecondaryButton
                            size="small"
                            data-testid="price-variation-edit-button"
                            onClick={() => setShowEditPriceVariationModal(true)}
                        >
                            <EditIcon />
                        </SecondaryButton>
                        <SecondaryButton
                            size="small"
                            data-testid="price-variation-delete-button"
                            onClick={deletePriceVariation}
                        >
                            <TrashIcon />
                        </SecondaryButton>
                    </div>
                )}
            </div>

            {experience && priceVariation?.type === PriceVariationType.BASE && (
                <CreatePriceVariation
                    experience={experience}
                    priceVariation={priceVariation}
                    schedulePurchaseRules={schedulePurchaseRules}
                    onSubmit={handlePriceVariationChange}
                    onPurchaseRuleGroupClick={handlePurchaseRuleGroupChange}
                />
            )}

            {experience && priceVariation?.type === PriceVariationType.ADJUSTMENT && (
                <SchedulePriceVariation
                    experience={experience}
                    priceVariation={priceVariation}
                    showEditPriceVariationModal={showEditPriceVariationModal}
                    setShowEditPriceVariationModal={setShowEditPriceVariationModal}
                    onChange={handlePriceVariationChange}
                />
            )}
        </div>
    ) : null;
};
